import React, { useEffect, useState } from 'react';
import { useUser } from './User';
import { calculateStartAndEndDate } from '../libs/utils';
import {
  closeToolRecoveryRequest,
  contactToolRecoveryOwner,
  getShopNames,
  getToolRecoveryRequests,
  processToolRecoveryRequest,
  transferDisputeInfo,
} from '../libs/db-lib';
import moment from 'moment';

export interface TRRsInterface {
  areTRRsLoading: boolean;
  toolRecoveryRequests: any[] | null;
  toolRecoveryShops: any[] | null;
  handleRefresh(overrideAreTRRsLoading: boolean, showClosed: boolean, startDate: string, endDate: string): any;
  handleContactOwner(toolID: string): any;
  handleProcessRequest(toolID: string, approved: boolean, reason?: string | undefined): any;
  handleCloseRequest(toolID: string, reason: string): any;
  handleTransferDisputeInfo(toolID: string): any;
}

export const TRRsContext = React.createContext<TRRsInterface>({
  areTRRsLoading: true,
  toolRecoveryRequests: null,
  toolRecoveryShops: null,
  handleRefresh: (overrideAreTRRsLoading, showClosed, startDate, endDate) => null,
  handleContactOwner: (toolID) => null,
  handleProcessRequest: (toolID, approved, reason) => null,
  handleCloseRequest: (toolID, reason) => null,
  handleTransferDisputeInfo: (toolID) => null,
});

export const useTRRs = () => {
  return React.useContext(TRRsContext);
};

type TRRsProviderType = {
  children: JSX.Element;
};

export function TRRsProvider({ children }: TRRsProviderType) {
  const { user } = useUser();

  const [areTRRsLoading, setAreTRRsLoading] = useState<boolean>(true);
  const [toolRecoveryRequests, setToolRecoveryRequests] = useState<any[]>([]);
  const [toolRecoveryShops, setToolRecoveryShops] = useState<any[]>([]);

  useEffect(() => {
    if (user && ['ADMIN', 'MANAGER'].includes(user.userType)) {
      const tomorrow = `${moment().add(1, 'day').format('YYYY-MM-DD')}T23:59:59.999Z`; // EOD
      const last30StartDate = `${moment()
        .subtract(30, 'days')
        .format('YYYY-MM-DD')}T00:00:00.000Z`; // Start of day
      handleRefresh(true, false, last30StartDate, tomorrow);
      const refreshInterval = setInterval(() => {
        handleRefresh(false, false, last30StartDate, tomorrow);
      }, 300000); // refreshing every 5 minutes
      return () => {
        clearInterval(refreshInterval);
      };
    } else {
      setToolRecoveryRequests([]);
    }
  }, [user]);

  const handleRefresh = async (overrideAreTRRsLoading: boolean, showClosed: boolean, startDate: string, endDate: string) => {
    if (!user || (user && !['ADMIN', 'MANAGER'].includes(user.userType))) {
      return;
    }

    if (overrideAreTRRsLoading) {
      setAreTRRsLoading(true);
    }

    const response = await getToolRecoveryRequests(showClosed, startDate, endDate);

    let newShops: any = {};

    if (!response.error) {
      const shopIDs = new Set<string>();

      response.forEach((trr: any) => {
        if (trr.shopID) {
          shopIDs.add(trr.shopID);
        }
        if (trr.ownerShopID) {
          shopIDs.add(trr.ownerShopID);
        }
      });

      if (shopIDs.size) {
        newShops = await getShopNames(Array.from(shopIDs));
      }

      setToolRecoveryRequests(response);

      setToolRecoveryShops(prev => ({
        ...prev,
        ...newShops,
      }));
    }

    setAreTRRsLoading(false);
    return response;
  };

  const handleContactOwner = async (toolID: string) => {
    if (!user || (user && !['ADMIN', 'MANAGER'].includes(user.userType))) {
      return;
    }

    const response = await contactToolRecoveryOwner(toolID);

    if (!response.error) {
      setToolRecoveryRequests(prev => prev.map((request: any) => request?.toolID === toolID ?
        {
          ...request,
          ownerTicketID: response.ownerTicketID,
          status: 'PENDING',
          deadline: response.deadline
        }
        :
        request
      ));
    }

    return response;
  }

  const handleProcessRequest = async (toolID: string, approved: boolean, reason?: string | undefined) => {
    if (!user || (user && !['ADMIN', 'MANAGER'].includes(user.userType))) {
      return;
    }

    let response;

    if (approved) {
      response = await processToolRecoveryRequest(toolID, 'APPROVED');
    } else {
      response = await processToolRecoveryRequest(toolID, 'REJECTED', reason);
    }

    if (!response.error) {
      setToolRecoveryRequests(prev => prev.filter((request: any) => request.toolID !== toolID));
    }

    return response;
  }

  const handleCloseRequest = async (toolID: any, reason: string) => {
    if (!user || (user && !['ADMIN', 'MANAGER'].includes(user.userType))) {
      return;
    }

    const response = await closeToolRecoveryRequest(toolID, reason);

    if (!response.error) {
      setToolRecoveryRequests(prev => prev.filter((request: any) => request.toolID !== toolID));
    }

    return response;
  };

  const handleTransferDisputeInfo = async (toolID: string) => {
    if (!user || (user && !['ADMIN', 'MANAGER'].includes(user.userType))) {
      return;
    }

    const response = await transferDisputeInfo(toolID);

    if (!response.error) {
      setToolRecoveryRequests(prev => prev.map((request: any) => {
        if (request.toolID === toolID) {
          return { ...request, disputeInfoTransferred: true };
        } else {
          return request;
        }
      }))
    }

    return response;
  }

  const provider = {
    areTRRsLoading,
    toolRecoveryRequests,
    toolRecoveryShops,
    handleRefresh,
    handleContactOwner,
    handleProcessRequest,
    handleCloseRequest,
    handleTransferDisputeInfo,
  };

  return <TRRsContext.Provider value={provider}>{children}</TRRsContext.Provider>;
}
